import type { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import { builder } from "@builder.io/react";
import {
  BuilderHubUrlTargeting,
  ContentHubTaxonomyContext,
  GenericPage,
  getHubPageUrlForTargeting,
  getServiceContentHubSlugs,
  ServiceContentHubIndexView,
  mergeTaxonomyTree,
  mergedTreeToBuildKitTaxonomies,
} from "@orlandohealth/build-kit";
import { slugify } from "@orlandohealth/utils";

interface ServiceContentHubIndexProps {
  taxonomies: { name: string; slug: string; taxonomies?: any[] }[];
  contentHubBasePath: string;
  content: any;
  url: string;
  siteProperties: any;
  metadata?: any;
  schemaEnrichment?: any;
  navData?: any;
  dictionaryLabels?: any;
  breadcrumbs?: any;
  alerts?: any;
  symbols?: any[];
  builderLocale?: string;
  locale?: string;
  defaultLocale?: string;
}

export const getStaticPaths: GetStaticPaths = async ({
  locales = ["en_US"],
}) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  const paths: { params: { serviceSlug: string }; locale: string }[] = [];
  const originalApiKey = builder.apiKey;
  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
  try {
    for (const locale of locales) {
      const slugs = await getServiceContentHubSlugs(locale);
      for (const serviceSlug of slugs) {
        paths.push({ params: { serviceSlug }, locale });
      }
    }
  } finally {
    builder.apiKey = originalApiKey;
  }
  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps<ServiceContentHubIndexProps> = async ({
  params,
  locale,
  defaultLocale,
  preview,
  previewData = {},
}) => {
  const serviceSlug = params?.serviceSlug as string;
  if (!serviceSlug) {
    return { notFound: true, revalidate: 60 };
  }
  const hubPath = `/services/${serviceSlug}/content-hub`;
  const originalApiKey = builder.apiKey;

  const isInBuilder =
    !!(previewData as Record<string, unknown>)?.["builder.space"] ||
    !!(previewData as Record<string, unknown>)?.["builder.frameEditing"];

  const base = await GenericPage.getStaticProps(
    hubPath,
    locale,
    defaultLocale,
    "page",
    preview || isInBuilder
  );

  if (!base.props.content && !preview && !isInBuilder) {
    return { notFound: true, revalidate: 60 };
  }

  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
  const page = base.props.content;

  const siteProperties = await builder
    .get("site-properties", {
      locale: locale || "en",
      query: { name: process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "fl" },
      options: {
        includeRefs: true,
        vercelPreview: true,
        locale: locale || "en",
        enrich: true,
      },
    })
    .promise();

  // Fetch taxonomy WITHOUT locale so name fields stay as Builder LocalizedValue
  // objects; mergeTaxonomyTree resolves localized display names while preserving
  // Default-locale `defaultName` values for slug generation.
  let taxonomyData = null;
  if (page?.data?.taxonomy?.referenceId) {
    builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
    taxonomyData = await builder
      .get("taxonomy", {
        options: { includeRefs: true },
        query: { id: page?.data?.taxonomy?.referenceId },
        apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
      })
      .promise();
  } else if (siteProperties?.data?.taxonomies?.id) {
    builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
    taxonomyData = await builder
      .get("taxonomy", {
        options: { includeRefs: true },
        query: { id: siteProperties.data.taxonomies.id },
        apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
      })
      .promise();
  } else if (siteProperties?.data?.taxonomies?.taxonomies) {
    taxonomyData = { data: siteProperties.taxonomies };
  } else {
    builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
    taxonomyData = await builder
      .get("taxonomy", {
        options: { includeRefs: true },
        query: { name: "content-hub" },
        apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
      })
      .promise();
  }

  builder.apiKey = originalApiKey;

  const merged = mergeTaxonomyTree(taxonomyData?.data?.taxonomies, locale || defaultLocale || "en_US");
  const taxonomies = mergedTreeToBuildKitTaxonomies(merged, slugify);

  return {
    ...base,
    props: {
      ...base.props,
      taxonomies,
      contentHubBasePath: hubPath,
    },
  };
};

const ServiceContentHubIndexPage: React.FC<ServiceContentHubIndexProps> = (props) => {
  const hubTargetingUrl =
    getHubPageUrlForTargeting(props.content) ?? props.contentHubBasePath;

  return (
    <ContentHubTaxonomyContext.Provider
      value={{
        taxonomyTree: props.taxonomies,
        contentHubBasePath: props.contentHubBasePath,
      }}
    >
      <BuilderHubUrlTargeting hubUrlPath={hubTargetingUrl}>
        <ServiceContentHubIndexView {...props} />
      </BuilderHubUrlTargeting>
    </ContentHubTaxonomyContext.Provider>
  );
};

export default ServiceContentHubIndexPage;
